import { ProviderResults, type ProviderCandidate } from './provider-results';
import { LoadingNotice, SearchSkeleton } from './loading';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BOOK_LANGUAGE_NAMES_EN } from '@living-library/contracts';
import { AdminApiError } from '../admin-api';
import { button, input } from './editors';
import { decisionSender, type Book, type Request, type Submission } from './model';

type Provider = 'nkp_skc' | 'open_library' | 'isbndb';
interface Options {
  language: string;
  providers: { id: Provider; label: string; available: boolean }[];
}
interface Results {
  items: ProviderCandidate[];
  nextPage: number | null;
  limitReached: boolean;
  excludedCount: number;
}
export function providerError(error: unknown) {
  if (error instanceof AdminApiError) {
    const messages: Record<string, string> = {
      CATALOGUE_PROVIDER_UNAVAILABLE:
        'This provider is unavailable. Retry later; no other provider was searched.',
      CATALOGUE_PROVIDER_QUOTA_EXHAUSTED:
        'This provider’s quota is exhausted. Retry later or choose another available provider.',
      CATALOGUE_PROVIDER_IDENTITY_CONFLICT:
        'This edition or ISBN is already stored with a different catalogue identity. Review the existing shared editions before matching.',
      CATALOGUE_PROVIDER_LANGUAGE_MISMATCH:
        'The exact edition has a different or unknown language. Choose another edition.',
      CATALOGUE_EDITION_IDENTITY_CONFLICT:
        'The edition’s language, ISBN or binding conflicts with the submission. Choose another edition.',
      CATALOGUE_PROVIDER_EDITION_NOT_FOUND:
        'That exact edition is no longer available from the provider. Search again.',
      CATALOGUE_VERSION_CONFLICT:
        'The submission or book changed. Reload this request before matching.',
      CATALOGUE_SUBMISSION_STATE:
        'This request is no longer pending. Reload it to see the latest decision.',
      CATALOGUE_BOOK_NOT_FOUND:
        'This catalogue edition is unavailable or hidden. Choose another edition.',
      CATALOGUE_QUERY_INVALID:
        'Enter at least three characters, or a valid ISBN when searching by ISBN.',
    };
    if (messages[error.code]) return messages[error.code];
  }
  return 'The provider request could not be completed. Retry your search or load the edition again.';
}
export function ProviderMatcher({
  submission,
  request,
  disabled,
  select,
  onBusy,
}: {
  submission: Submission;
  request: Request;
  disabled: boolean;
  select(book: Book | null): void;
  onBusy(busy: boolean): void;
}) {
  const [options, setOptions] = useState<Options | null>(null),
    [provider, setProvider] = useState<Provider | ''>('');
  const [query, setQuery] = useState(submission.metadata.isbn ?? submission.metadata.title);
  const [scope, setScope] = useState(submission.metadata.isbn ? 'isbn' : 'title');
  const [results, setResults] = useState<Results | null>(null),
    [page, setPage] = useState(1);
  const [busy, setBusy] = useState<'search' | 'prepare' | null>(null),
    [error, setError] = useState(''),
    [reloadOptions, setReloadOptions] = useState(0);
  const generation = useRef(0);
  const searchButton = useRef<HTMLButtonElement>(null);
  const [resultVersion, setResultVersion] = useState(0);
  const [retryCandidate, setRetryCandidate] = useState<ProviderCandidate | null>(null);
  const requestedPage = useRef(1);
  const send = useMemo(() => decisionSender(request), [request]);
  const base = `/submissions/${submission.id}`;
  useEffect(() => {
    let current = true;
    if (submission.status !== 'pending') return;
    setError('');
    request<Options>(`${base}/provider-options?expectedVersion=${submission.version}`)
      .then((result) => {
        if (!current) return;
        setOptions(result);
        setProvider(result.providers.find((p) => p.available)?.id ?? result.providers[0]?.id ?? '');
        setError('');
      })
      .catch((e) => {
        if (current) setError(providerError(e));
      });
    return () => {
      current = false;
      generation.current++;
    };
  }, [base, request, submission.version, submission.status, reloadOptions]);
  useEffect(() => {
    onBusy(busy === 'prepare');
    return () => onBusy(false);
  }, [busy, onBusy]);
  const clear = () => {
    generation.current++;
    setResults(null);
    setError('');
    select(null);
  };
  const search = async (wanted = 1) => {
    const current = ++generation.current;
    setBusy('search');
    setRetryCandidate(null);
    requestedPage.current = wanted;
    setError('');
    select(null);
    try {
      const params = new URLSearchParams({
        expectedVersion: String(submission.version),
        provider,
        q: query.trim(),
        searchIn: scope,
        page: String(wanted),
      });
      const response = await request<Results>(`${base}/provider-search?${params}`);
      if (current === generation.current) {
        setResults(response);
        setResultVersion((version) => version + 1);
        setPage(wanted);
      }
    } catch (e) {
      if (current === generation.current) setError(providerError(e));
    } finally {
      if (current === generation.current) setBusy(null);
    }
  };
  const prepare = async (candidate: ProviderCandidate) => {
    const current = ++generation.current;
    setBusy('prepare');
    setRetryCandidate(candidate);
    setError('');
    select(null);
    try {
      const book = await send<Book>(`${base}/provider-edition`, {
        expectedVersion: submission.version,
        provider: candidate.source,
        sourceId: candidate.sourceId,
        isbn: candidate.isbn,
      });
      if (current === generation.current) select(book);
    } catch (e) {
      if (current === generation.current) setError(providerError(e));
    } finally {
      if (current === generation.current) setBusy(null);
    }
  };
  const regional = ['sk', 'cs'].includes(submission.metadata.language);
  const available = options?.providers.some((p) => p.id === provider && p.available);
  const providerLabel = options?.providers.find((p) => p.id === provider)?.label ?? 'provider';
  if (submission.status !== 'pending')
    return (
      <p className="py-6 text-sm text-ink-muted">
        Provider matching is available while a publication request is pending.
      </p>
    );
  return (
    <section aria-label="Search external catalogue" className="pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif text-2xl">Find a catalogue edition</h3>
        {!options && !error ? (
          <LoadingNotice label="Checking providers…" />
        ) : (
          <span className="rounded-full border border-line/65 px-3 py-1 text-xs font-semibold text-ink-muted">
            {BOOK_LANGUAGE_NAMES_EN[submission.metadata.language] ?? submission.metadata.language}
          </span>
        )}
      </div>
      <p className="mt-2 text-sm leading-6 text-ink-muted">
        {regional
          ? 'Search NKP for this Slovak or Czech edition.'
          : 'Search a provider in the submitted book’s language, then compare the edition.'}
      </p>
      <fieldset disabled={disabled || !!busy || !options} className="mt-5 min-w-0">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold">
            Provider
            <select
              className={input}
              value={provider}
              disabled={!options || regional}
              onChange={(e) => {
                clear();
                setProvider(e.target.value as Provider);
              }}
            >
              {!options && <option value="">Loading providers…</option>}
              {options?.providers.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.available}>
                  {p.label}
                  {p.available ? '' : ' — unavailable'}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold">
            Search by
            <select
              className={input}
              value={scope}
              onChange={(e) => {
                clear();
                setScope(e.target.value);
              }}
            >
              <option value="title">Title</option>
              <option value="author">Author</option>
              <option value="isbn">ISBN</option>
              <option value="all">All fields</option>
            </select>
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="block min-w-0 basis-64 grow text-sm font-semibold">
            Search catalogue
            <input
              className={input}
              value={query}
              maxLength={160}
              onChange={(e) => {
                clear();
                setQuery(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && available && query.trim().length >= 3) {
                  e.preventDefault();
                  void search();
                }
              }}
            />
          </label>
          <button
            type="button"
            ref={searchButton}
            className="admin-button-primary min-w-36"
            disabled={!available || query.trim().length < 3}
            onClick={() => void search()}
          >
            {busy === 'search' ? 'Searching…' : 'Search provider'}
          </button>
        </div>
      </fieldset>
      {options && !available && (
        <p className="mt-4 rounded-xl border border-warning/30 p-4 text-sm leading-6 text-warning">
          {regional
            ? 'NKP is not enabled in this environment. Provider matching is unavailable for this language.'
            : 'Select an available provider to search.'}
        </p>
      )}
      {busy && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-thread-soft/50 p-4">
          <LoadingNotice
            key={busy}
            label={busy === 'search' ? `Searching ${providerLabel}…` : 'Loading the exact edition…'}
            detail={
              busy === 'search'
                ? 'Checking editions in the submitted language.'
                : 'Fetching and saving provider metadata for your comparison.'
            }
            slow
          />
          {busy === 'search' && (
            <button
              type="button"
              className={button}
              onClick={() => {
                generation.current++;
                setBusy(null);
                requestAnimationFrame(() => searchButton.current?.focus());
              }}
            >
              Cancel search
            </button>
          )}
        </div>
      )}
      {error && (
        <div className="mt-5 rounded-xl border border-warning/30 bg-warning/5 p-4">
          <p role="alert" className="text-sm leading-6 text-warning">
            {error}
          </p>
          <button
            type="button"
            className={`${button} mt-3`}
            disabled={disabled || !!busy}
            onClick={() =>
              options
                ? retryCandidate
                  ? void prepare(retryCandidate)
                  : void search(requestedPage.current)
                : setReloadOptions((n) => n + 1)
            }
          >
            {options
              ? retryCandidate
                ? 'Retry loading edition'
                : 'Retry search'
              : 'Retry providers'}
          </button>
        </div>
      )}
      <div aria-busy={!!busy} className="mt-6">
        {busy === 'search' && !results && <SearchSkeleton />}
        {results && (
          <fieldset
            disabled={disabled || !!busy}
            className="min-w-0 space-y-4"
            aria-label="Provider search results"
          >
            {results.items.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line/65 pt-5 text-xs text-ink-muted">
                <p role="status" className="font-semibold text-ink">
                  {results.items.length} possible edition{results.items.length === 1 ? '' : 's'}
                </p>
                <p>
                  {providerLabel} · Page {page}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-line px-5 py-8 text-center">
                <h4 className="font-serif text-xl" role="status">
                  No eligible editions on this page
                </h4>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-ink-muted">
                  Try a shorter title, the author’s name or an ISBN. You can also review this
                  submission in Edit &amp; publish.
                </p>
              </div>
            )}
            {results.excludedCount > 0 && (
              <p className="text-xs leading-5 text-ink-muted">
                Results with another or unknown language, unavailable records, and work-only records
                are excluded.
              </p>
            )}
            <div className={busy === 'search' ? 'opacity-50' : undefined}>
              <ProviderResults
                key={resultVersion}
                items={results.items}
                prepare={(book) => void prepare(book)}
                preparing={busy === 'prepare'}
              />
            </div>
            {(page > 1 || results.nextPage) && (
              <div className="flex items-center justify-between gap-3 border-t border-line/65 pt-4">
                <button
                  type="button"
                  className={button}
                  disabled={page === 1}
                  onClick={() => void search(page - 1)}
                >
                  Previous page
                </button>
                <span className="text-xs text-ink-muted">Page {page}</span>
                <button
                  type="button"
                  className={button}
                  disabled={!results.nextPage}
                  onClick={() => void search(results.nextPage!)}
                >
                  Next page
                </button>
              </div>
            )}
            {results.limitReached && (
              <p className="text-sm text-ink-muted">
                Provider search limit reached. Narrow the title, author or ISBN.
              </p>
            )}
          </fieldset>
        )}
        {!results && !busy && !error && (!options || available) && (
          <p className="border-t border-line/65 pt-4 text-xs leading-6 text-ink-muted">
            Search results appear here. Opening a result lets you check its details before loading
            it into the catalogue. The reader must explicitly accept a proposed match.
          </p>
        )}
      </div>
    </section>
  );
}
